import { useMemo, useState } from 'react'
import { usePortalClient } from '../hooks/usePortalClient'
import { calculateBenchmarkGap, calculateRiskMaturityAlignment, getTopPriorities } from '../../../shared/utils/scoring'
import { getMarketSegmentId } from '../../../shared/utils/marketSegment'
import { MATURITY_LEVELS, getMaturityLevelByScore } from '../../../shared/utils/maturityLevels'

function getCmmiLevel(score) {
  if (score == null) return null
  return getMaturityLevelByScore(score)
}

function PortalResults() {
  const { client, loading } = usePortalClient()
  const [showMethodology, setShowMethodology] = useState(false)

  const completedAssessments = (client?.assessments || [])
    .filter(a => a.status === 'completed')
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))

  const assessment = completedAssessments[0]

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-400" />
      </div>
    )
  }

  // Locked state — no completed assessment
  if (!assessment || !assessment.scores) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-10 text-center">
        <div className="w-[72px] h-[72px] rounded-[20px] flex items-center justify-center mb-5 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
          <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-base font-bold mb-1.5 text-gray-900 dark:text-white">Assessment Results Pending</h2>
        <p className="text-sm leading-relaxed max-w-sm text-gray-500 dark:text-gray-400">
          Your security advisor is completing the assessment review. Results will be available here once released. You'll receive a notification when they're ready.
        </p>
      </div>
    )
  }

  const { scores } = assessment
  const currentScore = Math.round(scores.overall?.score || 0)
  const currentLevel = getCmmiLevel(currentScore)
  const industry = assessment.responses?.businessScoping?.['bs-0']?.value || client?.industry || 'Other'
  const marketSegment = getMarketSegmentId(assessment.responses?.businessScoping)

  const benchmarkResult = useMemo(() =>
    calculateBenchmarkGap(scores.overall.score, industry, marketSegment),
    [scores.overall.score, industry, marketSegment]
  )

  const alignments = useMemo(() =>
    calculateRiskMaturityAlignment(scores.riskAppetite, scores.maturity),
    [scores.riskAppetite, scores.maturity]
  )

  const priorities = useMemo(() =>
    getTopPriorities(scores, alignments, benchmarkResult),
    [scores, alignments, benchmarkResult]
  )

  // Score trend
  const previousAssessment = completedAssessments[1]
  const previousScore = previousAssessment?.scores?.overall?.score
  const scoreTrend = scores.overall.score != null && previousScore != null
    ? Math.round(scores.overall.score - previousScore)
    : null

  // Category scores for strengths/improvements
  const categoryScores = scores.maturity?.categoryScores || {}
  const sortedCategories = Object.entries(categoryScores)
    .map(([id, data]) => ({ id, label: data.label || id, score: Math.round(data.score || 0) }))
    .sort((a, b) => b.score - a.score)

  const strengths = sortedCategories.slice(0, 4)
  const improvements = [...sortedCategories].sort((a, b) => a.score - b.score).slice(0, 4)

  // Benchmark percentile
  const percentile = benchmarkResult?.percentile ?? (benchmarkResult?.gap != null ? Math.max(0, Math.min(100, 50 + benchmarkResult.gap)) : null)
  const completedDate = assessment.completedAt
    ? new Date(assessment.completedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null

  return (
    <div className="space-y-5">
      {/* Score card with CMMI visualization */}
      <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Navy gradient header */}
        <div className="p-5" style={{ background: 'linear-gradient(135deg, #012438, #213C4E)' }}>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs uppercase tracking-wider" style={{ color: '#7E8F9C', letterSpacing: '1px' }}>
                {assessment.title || 'Security Assessment'}
              </p>
              <p className="text-lg font-bold text-white mt-1">Maturity Score: {currentScore} / 100</p>
              {completedDate && (
                <p className="text-xs mt-1" style={{ color: '#BCC5CD' }}>Completed {completedDate}</p>
              )}
            </div>
            {currentLevel && (
              <div className="text-center px-4 py-2 rounded-xl" style={{ background: currentLevel.color + '20' }}>
                <p className="text-2xl font-extrabold leading-none" style={{ color: currentLevel.color }}>L{currentLevel.level}</p>
                <p className="text-xs mt-1" style={{ color: '#BCC5CD' }}>{currentLevel.label}</p>
              </div>
            )}
          </div>
        </div>

        {/* CMMI level bar */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1">
            {MATURITY_LEVELS.map(level => {
              const isActive = currentLevel?.level === level.level
              const isPast = currentLevel && level.level < currentLevel.level
              return (
                <div key={level.level} className="flex-1 flex flex-col items-center gap-1.5">
                  <div
                    className="h-1.5 w-full rounded-full"
                    style={{
                      backgroundColor: isPast || isActive ? level.color : '#F1F5F9',
                      opacity: isPast ? 0.4 : 1
                    }}
                  />
                  <p className="text-center" style={{ fontSize: '9px', fontWeight: isActive ? 700 : 400, color: isActive ? level.color : '#94A3B8' }}>
                    L{level.level}<br />{level.label}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {/* Interpretive text */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold mb-1.5 text-gray-900 dark:text-white">What This Means for {client?.name}</p>
          <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            At <strong className="text-gray-900 dark:text-white">CMMI Level {currentLevel?.level} ({currentLevel?.label})</strong>, your organization has
            {currentLevel?.level >= 4
              ? ' moved beyond documented processes into actively measured and controlled security operations. Your controls are functioning, your team has defined responsibilities, and you have visibility into your security posture.'
              : currentLevel?.level >= 3
              ? ' documented and standardized security processes. Consistency is improving across your organization.'
              : currentLevel?.level >= 2
              ? ' basic security processes forming. There is room for significant improvement in formalizing controls.'
              : ' reactive, ad hoc security processes. Establishing foundational controls should be the immediate priority.'
            }
            {percentile != null && (
              <> You are performing in the <strong className="text-gray-900 dark:text-white">{Math.round(percentile)}th percentile</strong> among {industry.toLowerCase()} organizations of similar size.</>
            )}
          </p>
        </div>

        {/* Peer comparison bar */}
        {percentile != null && (
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs font-semibold mb-3 text-gray-900 dark:text-white">Industry Peer Comparison</p>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="relative h-6 rounded-full bg-gray-50 dark:bg-gray-800/50">
                  <div
                    className="absolute left-0 top-0 h-full rounded-full"
                    style={{ width: `${percentile}%`, background: 'linear-gradient(90deg, #012438, #213C4E)' }}
                  />
                  <div
                    className="absolute top-[-4px] w-0.5 h-8 bg-primary-400"
                    style={{ left: `${percentile}%`, transform: 'translateX(-50%)' }}
                  />
                  <div
                    className="absolute top-[-4px] w-0.5 h-8 bg-gray-200 dark:bg-gray-700"
                    style={{ left: '50%', transform: 'translateX(-50%)' }}
                  />
                </div>
                <div className="flex justify-between mt-1.5">
                  <span className="text-xs text-gray-400 dark:text-gray-500">0th percentile</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">Median</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">100th percentile</span>
                </div>
              </div>
              <div className="text-center flex-shrink-0 w-20">
                <p className="text-2xl font-extrabold text-gray-900 dark:text-white">{Math.round(percentile)}th</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Percentile</p>
              </div>
            </div>
            <p className="text-xs mt-2 text-gray-400 dark:text-gray-500">
              Compared to: {industry} organizations of similar size and regulatory scope
            </p>
          </div>
        )}

        {/* Strengths / Priority Improvements */}
        {(strengths.length > 0 || improvements.length > 0) && (
          <div className="grid grid-cols-2 border-b border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-r border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1.5 mb-3">
                <svg className="w-4 h-4" style={{ color: '#22C55E' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs font-bold" style={{ color: '#22C55E' }}>Top Strengths</span>
              </div>
              {strengths.map(s => (
                <p key={s.id} className="text-xs py-0.5 text-gray-500 dark:text-gray-400">• {s.label} ({s.score})</p>
              ))}
            </div>
            <div className="px-5 py-4">
              <div className="flex items-center gap-1.5 mb-3">
                <svg className="w-4 h-4" style={{ color: '#F59E0B' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-xs font-bold" style={{ color: '#F59E0B' }}>Priority Improvements</span>
              </div>
              {improvements.map(s => (
                <p key={s.id} className="text-xs py-0.5 text-gray-500 dark:text-gray-400">• {s.label} ({s.score})</p>
              ))}
            </div>
          </div>
        )}

        {/* Score trend footer */}
        {scoreTrend !== null && (
          <div className="px-5 py-3 flex items-center gap-2">
            <span className="text-xs font-semibold" style={{ color: scoreTrend >= 0 ? '#22C55E' : '#EF4444' }}>
              {scoreTrend >= 0 ? '↑' : '↓'} {scoreTrend >= 0 ? '+' : ''}{scoreTrend} points
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">since your last assessment</span>
          </div>
        )}
      </div>

      {/* Methodology collapsible */}
      <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">How We Calculate Your Score</span>
          <svg
            className={`w-3.5 h-3.5 transition-transform text-gray-400 dark:text-gray-500 ${showMethodology ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showMethodology && (
          <div className="px-5 pb-5 text-xs leading-relaxed border-t border-gray-100 dark:border-gray-700 pt-3.5 text-gray-500 dark:text-gray-400">
            <p className="mb-2.5">
              Your security maturity score is derived from an assessment of <strong className="text-gray-900 dark:text-white">23 security domains</strong> with over 100 individual control questions. Each response is scored on a 4-point scale and weighted based on three contextual factors:
            </p>
            <div className="p-3 rounded-lg mb-2.5 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-xs font-semibold mb-1 text-gray-900 dark:text-white">Weighting Factors</p>
              <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                1. <strong>Industry vertical</strong> — domains weighted by sector-specific risk<br />
                2. <strong>Organization size</strong> — expectations calibrated for employee count and revenue<br />
                3. <strong>Stated risk appetite</strong> — declared risk tolerance adjusts thresholds
              </p>
            </div>
            <p>
              The 0–100 score maps to <strong className="text-gray-900 dark:text-white">CMMI-aligned maturity levels</strong> to give your leadership team a recognized framework for interpreting results. Peer benchmarks are calculated from anonymized assessment data across organizations of similar size, industry, and regulatory scope.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PortalResults
